import { type FC, useMemo, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  LinearProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  FormBuilder,
  lockSchema,
  SECRET_PLACEHOLDER,
} from "@/shared/components/Form";
import {
  useConnectBunnyByAccountMutation,
  useCurrentPlatformQuery,
  useUpdateBunnySettingsMutation,
} from "@/partner/entities/platforms";
import {
  extractMutationErrorDetail,
  extractMutationErrorExtension,
  resolveMutationErrorKey,
} from "@/partner/shared/lib";
import { makeBunnySettingsSchema } from "../model/bunnySchemas";
import { BunnyAccountConnectForm } from "./BunnyAccountConnectForm";

const CONNECTION_STATUS_KEYS: Record<"connected" | "notConnected", string> = {
  connected: "bunnySettings.status.connected",
  notConnected: "bunnySettings.status.notConnected",
};

/**
 * Коды отказа бекенда (extensions.code) и тексты под них, как в карточке
 * Cloudflare. Сообщение сервера наружу не идёт, для непокрытых кодов
 * общий текст со статусом Bunny в скобках.
 */
const MUTATION_ERROR_KEYS: Record<string, string> = {
  INVALID_BUNNY_SETTINGS: "bunnySettings.errors.invalidSettings",
  INVALID_BUNNY_API_KEY: "bunnySettings.errors.invalidApiKey",
  BUNNY_LIBRARY_NOT_FOUND: "bunnySettings.errors.libraryNotFound",
  BUNNY_API_ERROR: "bunnySettings.errors.apiError",
};

/**
 * Те же коды в режиме «Account API key». Отличается один:
 * INVALID_BUNNY_API_KEY здесь не «партнёр вписал не тот ключ» (ключ он
 * вообще не вводил), а «ключ созданной библиотеки ещё не разошёлся по
 * Bunny»: бекенд ждёт его несколько секунд и, если не дождался, отдаёт
 * этот код. Текст поэтому предлагает нажать Connect ещё раз и обещает,
 * что вторая библиотека не появится.
 */
const ACCOUNT_MUTATION_ERROR_KEYS: Record<string, string> = {
  ...MUTATION_ERROR_KEYS,
  INVALID_BUNNY_API_KEY: "bunnySettings.errors.libraryKeyNotReady",
  INVALID_BUNNY_ACCOUNT_KEY: "bunnySettings.errors.invalidAccountKey",
  BUNNY_LIBRARY_CREATE_FAILED: "bunnySettings.errors.libraryCreateFailed",
  BUNNY_PULL_ZONE_CONFIG_FAILED: "bunnySettings.errors.pullZoneConfigFailed",
};

const FALLBACK_ERROR_KEY = "bunnySettings.errors.apiError";

type BunnySettingsMode = "account" | "advanced";

/** Предел Bunny на имя библиотеки, как в buildBunnyLibraryName на бекенде. */
const BUNNY_LIBRARY_NAME_MAX_LENGTH = 64;

export const BunnySettingsCard: FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { data, refetch } = useCurrentPlatformQuery();
  const settings = data?.currentPlatform?.bunnySettings;

  // Подключённая платформа открывается на «Advanced»: там видно
  // сохранённые Library ID и CDN Hostname, то есть текущее состояние.
  // Неподключённая открывается на подключении по одному аккаунтному
  // ключу. Выбор пользователя (chosenMode) перебивает оба правила, а до
  // него режим следует данным, которые могли ещё не загрузиться.
  const [chosenMode, setChosenMode] = useState<BunnySettingsMode | null>(null);
  const mode = chosenMode ?? (settings?.connectedAt ? "advanced" : "account");

  // Пока Bunny подключён, поля заблокированы, ключи показаны точками;
  // кнопки «Отключить» здесь нет, редактирование открывает «Изменить ключи».
  const [editing, setEditing] = useState(false);
  const locked = Boolean(settings?.connectedAt) && !editing;

  // Имя, которое бекенд даст новой библиотеке: название платформы,
  // обрезанное до предела Bunny, а у платформы без названия — ottkit-<id>
  // (buildBunnyLibraryName в StreamingSettingsService). Считаем его здесь,
  // чтобы в подписи радио не оказалось «Create new library “”».
  const newLibraryName = useMemo(() => {
    const title = data?.currentPlatform?.title?.trim();
    const platformId = data?.currentPlatform?.id;
    const name = title || (platformId ? `ottkit-${platformId}` : "");
    return name.slice(0, BUNNY_LIBRARY_NAME_MAX_LENGTH);
  }, [data?.currentPlatform?.title, data?.currentPlatform?.id]);
  const schema = useMemo(() => {
    const base = makeBunnySettingsSchema(t);
    return locked ? lockSchema(base) : base;
  }, [t, locked]);

  // Библиотека, которую бекенд успел создать перед падением подключения:
  // её id приходит в extensions ошибки, и форма подставляет его в выбор,
  // чтобы повтор не завёл в аккаунте вторую.
  const [createdLibraryId, setCreatedLibraryId] = useState<string | null>(null);

  const showError = (error: unknown, errorKeys: Record<string, string>) => {
    enqueueSnackbar(
      t(
        resolveMutationErrorKey(
          error as Parameters<typeof resolveMutationErrorKey>[0],
          errorKeys,
          FALLBACK_ERROR_KEY
        ),
        {
          code: extractMutationErrorDetail(
            error as Parameters<typeof extractMutationErrorDetail>[0]
          ),
        }
      ),
      { variant: "error" }
    );
  };

  const onConnected = () => {
    enqueueSnackbar(t("bunnySettings.snackbar.connected"), {
      variant: "success",
    });
    setEditing(false);
    setCreatedLibraryId(null);
    refetch();
  };

  const showAccountError = (error: unknown) => {
    const libraryId = extractMutationErrorExtension(
      error as Parameters<typeof extractMutationErrorExtension>[0],
      "bunnyLibraryId"
    );
    if (libraryId) setCreatedLibraryId(libraryId);
    showError(error, ACCOUNT_MUTATION_ERROR_KEYS);
  };

  const [updateBunnySettings, { loading }] = useUpdateBunnySettingsMutation({
    onCompleted: onConnected,
    onError: (error) => showError(error, MUTATION_ERROR_KEYS),
  });

  const [connectBunnyByAccount, { loading: connecting }] =
    useConnectBunnyByAccountMutation({
      onCompleted: onConnected,
      onError: showAccountError,
    });

  const busy = loading || connecting;

  return (
    <Card sx={{ mt: 2 }}>
      {busy && <LinearProgress />}
      <CardHeader
        title={t("bunnySettings.cardTitle")}
        action={
          settings?.connectedAt ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                size="small"
                color="success"
                label={t(CONNECTION_STATUS_KEYS.connected)}
              />
              <Chip
                size="small"
                variant="outlined"
                label={settings.libraryId}
              />
            </Stack>
          ) : (
            <Chip size="small" label={t(CONNECTION_STATUS_KEYS.notConnected)} />
          )
        }
      />
      <CardContent>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          disabled={busy}
          sx={{ mb: 2 }}
          onChange={(_event, value: BunnySettingsMode | null) => {
            if (value) setChosenMode(value);
          }}
        >
          <ToggleButton value="account">
            {t("bunnySettings.mode.account")}
          </ToggleButton>
          <ToggleButton value="advanced">
            {t("bunnySettings.mode.advanced")}
          </ToggleButton>
        </ToggleButtonGroup>

        {mode === "account" ? (
          <BunnyAccountConnectForm
            platformTitle={newLibraryName}
            connecting={connecting}
            locked={locked}
            currentLibraryId={settings?.libraryId ?? null}
            preselectLibraryId={createdLibraryId}
            onError={showAccountError}
            onConnect={async (input) => {
              await connectBunnyByAccount({
                variables: {
                  input: {
                    accountApiKey: input.accountApiKey,
                    libraryId: input.libraryId,
                    replicationRegions: input.replicationRegions,
                  },
                },
              });
            }}
          />
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("bunnySettings.hint")}
            </Typography>
            <FormBuilder
              key={locked ? "locked" : "editable"}
              formId="bunny-settings-form"
              schema={schema}
              initialValues={{
                libraryId: settings?.libraryId ?? "",
                cdnHostname: settings?.cdnHostname ?? "",
                apiKey: locked ? SECRET_PLACEHOLDER : "",
                readOnlyApiKey: locked ? SECRET_PLACEHOLDER : "",
                cdnTokenKey: locked ? SECRET_PLACEHOLDER : "",
                accountApiKey: locked ? SECRET_PLACEHOLDER : "",
              }}
              onSubmit={async (values) => {
                await updateBunnySettings({
                  variables: {
                    input: {
                      libraryId: String(values.libraryId),
                      apiKey: String(values.apiKey),
                      readOnlyApiKey: String(values.readOnlyApiKey),
                      cdnHostname: String(values.cdnHostname),
                      cdnTokenKey: String(values.cdnTokenKey),
                      accountApiKey: values.accountApiKey
                        ? String(values.accountApiKey)
                        : null,
                    },
                  },
                });
              }}
              submitButton={!locked}
              resetButton={false}
              submitButtonText={t("bunnySettings.submit")}
              disabled={busy || locked}
              actionsAlign="end"
            />
          </>
        )}

        {/* Кнопка правки общая для обоих режимов: подключение по
            аккаунтному ключу тоже заменяет сохранённые ключи, и открывать
            его надо тем же явным шагом, что и ручную форму. */}
        {settings?.connectedAt && (
          <Button
            variant="outlined"
            sx={{ mt: 2 }}
            disabled={busy}
            onClick={() => setEditing(!editing)}
          >
            {editing
              ? t("bunnySettings.cancelEdit")
              : t("bunnySettings.editKeys")}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
